import DishCodeTag from '@/features/menu/DishCodeTag';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { removeLine, updateQty } from './cartSlice';
import QtyStepper from '@/components/ui/QtyStepper';
import Price from '@/components/ui/Price';
import { thumbPhoto } from '@/features/menu/photoVariant';
import ProductImage from '@/features/menu/ProductImage';
import { BLOCK_MESSAGE_KEYS, type CartLineBlockReason } from './cartAvailability';
import { catalogName } from '@/lib/localizeCatalog';
import type { CartLine, OptionGroup } from '@/types';

interface Props {
  line: CartLine;
  restaurantUuid: string;
  optionGroups?: OptionGroup[];
  restaurantLogoUrl: string | null;
  restaurantName: string;
  themeColor: string;
  /** Set when the back-office made this line un-orderable since it was added:
   *  the line is marked in red and must be removed before the order can go out
   *  (see cartAvailability.diagnoseCartLines). */
  blockReason?: CartLineBlockReason;
}

function resolveOptionLabels(line: CartLine, groups: OptionGroup[]): string[] {
  const labels: string[] = [];
  for (const sel of line.selectedOptions) {
    const group = groups.find((g) => g.id === sel.groupId);
    if (!group) continue;
    for (const entry of sel.options) {
      const choice = group.options.find((o) => o.id === entry.id);
      if (!choice) continue;
      labels.push(entry.qty > 1 ? `${entry.qty}× ${catalogName(choice)}` : catalogName(choice));
    }
  }
  return labels;
}

export default function CartLineItem({
  line,
  restaurantUuid,
  optionGroups = [],
  restaurantLogoUrl,
  restaurantName,
  themeColor,
  blockReason
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const resolvedLabels = resolveOptionLabels(line, optionGroups);
  // Recommander rebuilds lines without group context; fall back to the
  // denormalized snapshot stored on the order so labels still appear.
  const optionLabels = resolvedLabels.length > 0 ? resolvedLabels : (line.optionNames ?? []);
  return (
    <div
      className={`flex gap-3 py-2 ${
        blockReason
          ? 'border-2 border-sale-red bg-cream px-2 my-1'
          : 'border-b-[1px] border-ink/30'
      }`}
      data-testid={blockReason ? `cart-line-blocked-${line.lineId}` : undefined}
      data-block-reason={blockReason}
    >
      <div className="w-16 h-16 border-[1px] border-ink self-start overflow-hidden shrink-0">
        <ProductImage
          photo={thumbPhoto(line.snapshot.photo)}
          restaurantLogoUrl={restaurantLogoUrl}
          restaurantName={restaurantName}
          themeColor={themeColor}
          alt={catalogName(line.snapshot)}
          variant="thumb"
        />
      </div>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <div className="flex items-start justify-between gap-2">
          <h4 className="font-display text-ink leading-none m-0"><DishCodeTag name={catalogName(line.snapshot)} code={line.snapshot.code} size="sm" />{catalogName(line.snapshot)}</h4>
          <button
            type="button"
            onClick={() => dispatch(removeLine({ restaurantUuid, lineId: line.lineId }))}
            className="shrink-0 w-6 h-6 flex items-center justify-center bg-hot-pink text-[var(--on-accent)] border-[1px] border-ink text-sm font-mono leading-none"
            aria-label={t('cart.removeItem')}
          >×</button>
        </div>
        {blockReason && (
          <p
            role="alert"
            className="m-0 font-mono text-xs text-sale-red leading-tight"
          >
            ⚠ {t(BLOCK_MESSAGE_KEYS[blockReason])}
          </p>
        )}
        {optionLabels.length > 0 && (
          <ul className="list-none pl-0 m-0 font-mono text-xs text-ink/70 leading-tight">
            {optionLabels.map((label, i) => (
              <li key={i}>· {label}</li>
            ))}
          </ul>
        )}
        {line.note && (
          <p className="m-0 font-mono text-xs italic text-ink/60">“{line.note}”</p>
        )}
        <div className="flex items-center justify-between">
          <QtyStepper
            qty={line.qty}
            min={0}
            size="sm"
            onChange={(qty) => dispatch(updateQty({ restaurantUuid, lineId: line.lineId, qty }))}
          />
          <Price amountCents={line.unitPrice * line.qty} />
        </div>
      </div>
    </div>
  );
}
